import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {lazyInject} from "@athena-portal/core";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import * as moment from "moment";
import {
    FalconNavigatorHeader,
    ICustomerPortalService,
    Policy,
    POLICY_STATUS_MTA,
    PolicyProperty,
    PolicyPropertyEnum,
    PREMIUM_TYPE_ANNUAL,
    PremiumType,
    ProductsEnum,
    Renewal,
    Subscription,
    Types,
    UPFRONT_PAYMENT_THRESHOLD
} from "@falcon/services";
import {YourCarCoverPanel} from "./YourCarCoverPanel";
import {
    allowToRenderFutureMTAExplainedHeader,
    findPreviousPolicyVersionByAgreementVersion,
    getAmountToDisplay,
    getPolicyInteraction,
    validPolicy
} from "../../../../../CommonFunctions";
import {StorageSuccessModal} from "../../../../../components/modals/StorageSuccessModal";
import {EditCarMtaSuccessModalWordingGenerator} from "../yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {FutureMTAExplainedHeader} from "../yourCarsPage/FutureMTAExplainedHeader";
import {RouteComponentProps} from "react-router-dom";
import * as successModals from "../../../../../components/modals/SuccessModalRenderers";

interface IYourCarCoverPageParams {
    policyNumber: string;
}

interface IYourCarCoverPageProps extends RouteComponentProps<IYourCarCoverPageParams> {
    location: any;
}

interface IState {
    policies?: Policy[];
    allPolicies?: Policy[];
    subscription?: Subscription;
    renewalProposal?: Renewal;
    policiesProcessed: boolean;
    previousMonthlyPremium?: number;
    renewalEnabled: boolean;
}

@authorize
export class YourCarCoverPage extends React.Component<IYourCarCoverPageProps, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IYourCarCoverPageProps) {
        super(props);

        this.state = {
            policiesProcessed: false,
            renewalEnabled: false
        };
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Policy summary");

        this.customerPortalService.getPolicies(ProductsEnum.WOLF_MOTOR).subscribe(
            (policies: Policy[]) => {
                const filteredPolicies: Policy[] = policies
                    .filter((policy: Policy) => policy.policyNumber === this.props.match.params.policyNumber)
                    .sort((a, b) => moment(b.endDate).diff(a.endDate));

                this.setState({
                    policies: filteredPolicies
                });

                if (policies && policies.length > 0) {

                    this.customerPortalService.getRenewalProposal(policies[0].policyNumber).subscribe(
                        (renewalProposal: Renewal) => {
                            this.setState({
                                renewalProposal
                            })
                        }
                    );

                    this.customerPortalService.getSubscription().subscribe((subscription: Subscription) => {
                        this.setState({
                            subscription
                        });
                    });

                    this.customerPortalService.getPolicyPropertyByPropertyKey(this.props.match.params.policyNumber, PolicyPropertyEnum.PERFORM_RENEWAL).subscribe(
                        (property: PolicyProperty) => {
                            this.setState({
                                renewalEnabled: property.value === "true"
                            });
                        }
                    );
                }

                this.setState({
                    policies: filteredPolicies,
                    policiesProcessed: true
                });
            }
        );

        this.customerPortalService.getPolicyHistory().subscribe(
            (allPolicies: Policy[]) => {
                const previousPolicyVersion = findPreviousPolicyVersionByAgreementVersion(allPolicies, this.props.match.params.policyNumber);
                this.setState({allPolicies});

                if (previousPolicyVersion) {
                    const previousInstalments = previousPolicyVersion.interaction.paymentDetails.instalmentPlan.instalments[0];
                    const previousMonthlyPremium = previousInstalments.principal + previousInstalments.interest;
                    this.setState({previousMonthlyPremium});
                }
            }
        );
    }

    public render() {
        const {subscription, policies, policiesProcessed} = this.state;
        const dataLoaded = subscription && policies && policiesProcessed;
        const policyNumber = policies ? policies[policies?.length - 1].policyNumber : "";
        const policyEndDate: string = policies ? policies[policies?.length - 1].endDate : "";

        const showSuccessModal: boolean = window.sessionStorage.getItem("showPopup") === "true";

        if (policies) {
            const policy: Policy = policies[0];

            return (
                <React.Fragment>
                    {showSuccessModal && this.renderSuccessModal()}
                    <FalconHeader/>
                    <Overlay show={!dataLoaded}/>
                    <div className="page-contents">
                        <FalconNavigatorHeader
                            back={`/dashboard/annualMotor/${policy.policyNumber}`}
                            title={policies.length === 1 ? "Your car cover" : "Your car covers"}
                        />
                        <div className="page-heading-white car-cover-policy-number"><span
                            className="font-weight-light">Policy no: </span>{policyNumber}</div>
                        <br/>
                        {
                            allowToRenderFutureMTAExplainedHeader(policies[policies?.length - 1].interaction.process) &&
                            <FutureMTAExplainedHeader
                                policyNumber={this.props.match.params.policyNumber}
                                policyEndDate={policyEndDate}
                                upcomingMtaDate={policy.annualPolicyInfo.maxFutureMtaDate}
                            />
                        }
                        {dataLoaded && this.renderYourCarCoverPanels()}
                        {this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualMotor/${policy.policyNumber}`)}
                    </div>
                    <FalconFooter/>
                </React.Fragment>
            );
        } else {
            return <Overlay show={true}/>
        }
    }

    private renderSuccessModal = (): React.ReactNode => {
        const referrerJourney: string | null = window.sessionStorage.getItem("referrerJourney");

        switch (referrerJourney) {
            case "mtaChangeAddressAnnualMotor":
                return this.renderChangeAddressPopup();
            default:
                return null;
        }
    };

    private renderYourCarCoverPanels(): JSX.Element[] | void {
        const {policies, allPolicies, subscription} = this.state;

        if (!(policies && allPolicies && subscription)) {
            return;
        }

        return policies!.map((policy: Policy, index: number) => {
            let currentlyActivePolicy: Policy | undefined;
            // If there is a future MTA, fetch latest policy before future MTA.
            if (policy.status === POLICY_STATUS_MTA && moment().isBefore(policy.interaction.headerInfo.startDate)) {
                currentlyActivePolicy = validPolicy(this.state.allPolicies!, policy.policyNumber, ProductsEnum.WOLF_MOTOR);
            }

            return (
                <YourCarCoverPanel
                    key={index}
                    policy={policy}
                    currentlyActivePolicy={currentlyActivePolicy}
                    subscription={subscription!}
                    annualPolicyInfo={policy.annualPolicyInfo}
                    vehicleIndex={index}
                    renewalProposal={this.state.renewalProposal}
                    renewalEnabled={this.state.renewalEnabled}
                />
            );
        });
    }

    private renderChangeAddressPopup = (): React.ReactNode => {
        const interactionId: string | null = window.sessionStorage.getItem("sessionInteraction");
        const interaction: any = getPolicyInteraction(interactionId, this.state.policies);
        const {factors, uistate} = interaction;
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;
        if (factors.premiumType === PREMIUM_TYPE_ANNUAL) {
            return this.getChangeAddressContentForAnnualPlan(factors);
        } else {
            return this.getChangeAddressContentForMonthlyPlan(factors, uistate, instalmentsNotPaidOff);
        }
    };

    private getChangeAddressContentForAnnualPlan(factors: any) {
        let title: React.ReactNode;
        let modalContent: React.ReactNode;

        if (((factors.returnPremium && factors.returnPremium > 0) || (factors.additionalPremium && factors.additionalPremium > 0)) &&
            (factors.mtaAndDebtCombinedAdditionalAmount && factors.mtaAndDebtCombinedAdditionalAmount > 0)) {

            title = "Payment was successful";
            modalContent =
                <p>
                    <span
                        className="font-weight-bold">{getAmountToDisplay(factors.mtaAndDebtCombinedAdditionalAmount)}&nbsp;</span>
                    has been taken out from your card for changing address on your car policy. Your account is now up to
                    date.
                </p>
        } else if (factors.returnPremium && factors.returnPremium > 0) {

            title = "Your refund is on the way";
            modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you
                    <span className="font-weight-bold">&nbsp;{getAmountToDisplay(factors.returnPremium)}&nbsp;</span>
                    for changing your address. Please allow two days for it to arrive.
                </p>;
        } else if (factors.additionalPremium && factors.additionalPremium > 0) {

            title = "Payment was successful";
            modalContent =
                <p>
                    <span className="font-weight-bold">{getAmountToDisplay(factors.additionalPremium)}&nbsp;</span>
                    has been taken out from your card for changing address on your car policy.
                </p>
        } else {

            title = "All done";
            modalContent = <p><span className="font-weight-bold">Your new address has been changed and saved</span></p>
        }

        return <StorageSuccessModal title={title} explanationWording={modalContent}
                                    anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                                    explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}/>
    }

    private getChangeAddressContentForMonthlyPlan(factors: any, uistate: any, instalmentsNotPaidOff: boolean) {
        let title: React.ReactNode;
        let modalContent: React.ReactNode;

        if (((factors.returnPremium && factors.returnPremium > 0) || (factors.additionalPremium && factors.additionalPremium > 0)) &&
            (factors.mtaAndDebtCombinedAdditionalAmount && factors.mtaAndDebtCombinedAdditionalAmount > 0) &&
            (factors.upfrontPaymentAmount && factors.upfrontPaymentAmount === 0 || factors.upfrontPaymentAmount === factors.mtaAndDebtCombinedAdditionalAmount /*mta in debt case*/)) {

            title = "Payment was successful";
            modalContent =
                <p>
                    <span
                        className="font-weight-bold">{getAmountToDisplay(factors.mtaAndDebtCombinedAdditionalAmount)}&nbsp;</span>
                    has been taken out from your card for changing address on your car policy. Your account is now up to
                    date.
                </p>
        } else if (factors.returnPremium && factors.returnPremium > 0 && factors.monthlyPremium === 0) {

            title = "Your refund is on the way";
            modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you{' '}
                    <span className="font-weight-bold">{getAmountToDisplay(factors.returnPremium)}</span>{' '}
                    for changing your address. Please allow two days for it to arrive.
                </p>;
        } else if (factors.returnPremium && factors.returnPremium > 0) {
            title = this.gerChangeAddressRefundContentTitle(factors, instalmentsNotPaidOff);
            modalContent = this.gerChangeAddressRefundContentModal(factors, instalmentsNotPaidOff);
        } else if (factors.additionalPremium && factors.additionalPremium > 0 && uistate.payFullPrice) {

            title = "Payment was successful";
            modalContent =
                <p>
                    <span className="font-weight-bold">{getAmountToDisplay(factors.additionalPremium)}&nbsp;</span>
                    has been taken out from your card for changing address on your car policy.
                </p>
        } else if (factors.additionalPremium && factors.additionalPremium > 0 && !uistate.payFullPrice) {
            title = this.gerChangeAddressAdditionalPremiumContentTitle(factors);
            modalContent = this.gerChangeAddressAdditionalPremiumContentModal(factors);
        } else {

            title = "All done";
            modalContent = <p><span className="font-weight-bold">Your new address has been changed and saved</span></p>
        }

        return <StorageSuccessModal title={title} explanationWording={modalContent}
                                    anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                                    explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}/>
    }

    private gerChangeAddressRefundContentTitle(factors: any, instalmentsNotPaidOff: boolean) {
        let title: React.ReactNode;

        if (instalmentsNotPaidOff && factors.monthlyPremium > 0) {
            title = "Your payment plan has been updated";
        } else {
            title = "Your refund is on the way";
        }

        return title;
    }

    private gerChangeAddressRefundContentModal(factors: any, instalmentsNotPaidOff: boolean) {
        if (factors.monthlyPremium === 0) {
            return (<p>
                We’ve saved your changes, and we’ll refund you{' '}
                <span className="font-weight-bold">{getAmountToDisplay(factors.returnPremium)}</span>{' '}
                for changing your address. Please allow two days for it to arrive.
            </p>);
        }
        return successModals.paymentPlanUpdatedRefundModal(factors.returnPremium, factors.monthlyPremium, this.state.previousMonthlyPremium, instalmentsNotPaidOff);
    }

    private gerChangeAddressAdditionalPremiumContentTitle(factors: any) {
        if (factors.upfrontPaymentAmount >= UPFRONT_PAYMENT_THRESHOLD) {
            return "Payment was successful";
        }
        return "All done";
    }

    private gerChangeAddressAdditionalPremiumContentModal(factors: any) {
        const monthlyPaymentAmount: number = factors.monthlyPaymentSpreadWithInterestAmount || factors.monthlyPaymentSpreadAmount;
        if (factors.upfrontPaymentAmount >= UPFRONT_PAYMENT_THRESHOLD) {
            return (<p>
                        <span
                            className="font-weight-semi-bold">{getAmountToDisplay(factors.upfrontPaymentAmount)}</span>{' '}
                        has been taken out from your account and{' '}
                        <span
                            className="font-weight-semi-bold">{getAmountToDisplay(monthlyPaymentAmount)}</span>{' '}
                        spread onto your monthly payments for changing your address. Your future monthly payment will be{' '}
                        <span
                            className="font-weight-semi-bold">{getAmountToDisplay(factors.monthlyPremium)} / month.</span>
                    </p>);
        }
        return successModals.paymentPlanUpdatedAdditionalPremiumModal(monthlyPaymentAmount, factors.monthlyPremium, this.state.previousMonthlyPremium);
    }

}



// WEBPACK FOOTER //
// ./src/pages/dashboard/annual/motor/yourCarCover/YourCarCoverPage.tsx